import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function WynbergPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Wynberg Painters Cape Town | Professional Painting Services | Victorian Home Specialists";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Wynberg properties. Expert Victorian home painting, heritage restoration, interior & exterior painting. Serving Wynberg Cape Town with premium quality finishes.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Wynberg painters, Victorian home painting, heritage restoration, Wynberg property painting, professional painters Cape Town');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/wynberg`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Wynberg Painters - Professional Painting Services Cape Town",
      "description": "Professional painting services for Wynberg properties with Victorian home expertise and heritage restoration.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/wynberg`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Wynberg",
        "description": "Specialized painting services for Wynberg's Victorian homes and heritage properties.",
        "areaServed": "Wynberg, Cape Town",
        "serviceType": "Victorian Home Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Wynberg')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Victorian Home Painting',
      description: 'Specialized painting for Wynberg\'s beautiful Victorian and Edwardian homes, preserving architectural character while providing modern protection and finishes.',
      price: 'From R12,000',
      features: ['Heritage color matching', 'Period-appropriate finishes', 'Decorative details', 'Architectural preservation']
    },
    {
      icon: 'ri-building-line',
      title: 'Heritage Restoration',
      description: 'Expert restoration painting for Wynberg\'s historic properties, using traditional techniques and materials to maintain authenticity and character.',
      price: 'From R15,000',
      features: ['Historical research', 'Traditional methods', 'Original color restoration', 'Conservation standards']
    },
    {
      icon: 'ri-brush-line',
      title: 'Interior Painting',
      description: 'Complete interior painting services for Wynberg homes, from modern apartments to period properties, with attention to detail and quality finishes.',
      price: 'From R8,000',
      features: ['Room preparation', 'Premium paints', 'Decorative finishes', 'Minimal disruption']
    },
    {
      icon: 'ri-palette-line',
      title: 'Exterior Painting',
      description: 'Professional exterior painting for Wynberg properties, protecting against weather while enhancing curb appeal and property value.',
      price: 'From R10,000',
      features: ['Weather protection', 'Surface preparation', 'Durable coatings', 'Color consultation']
    },
    {
      icon: 'ri-tools-line',
      title: 'Woodwork Restoration',
      description: 'Specialized restoration and painting of Wynberg\'s characteristic wooden features including sash windows, doors, and decorative trim.',
      price: 'From R5,000',
      features: ['Wood repair', 'Stripping & refinishing', 'Protective coatings', 'Period hardware']
    },
    {
      icon: 'ri-shield-line',
      title: 'Maintenance Programs',
      description: 'Regular maintenance painting services to protect your Wynberg property investment with scheduled inspections and touch-ups.',
      price: 'From R4,000',
      features: ['Scheduled visits', 'Preventive care', 'Priority service', 'Maintenance records']
    }
  ];

  const testimonials = [
    {
      name: 'Margaret Thompson',
      property: 'Victorian Villa',
      rating: 5,
      text: 'Exceptional work on our Victorian home. They understood the importance of preserving the original character while providing modern protection. The attention to detail was outstanding.',
      image: 'https://public.readdy.ai/ai/img_res/8751f392fbd40fce9612cda5d0001b13.jpg'
    },
    {
      name: 'David Roberts',
      property: 'Edwardian House',
      rating: 5,
      text: 'Outstanding restoration work on our Edwardian property. The team matched the original colors perfectly and the craftsmanship is superb. Highly recommend for heritage homes.',
      image: 'https://readdy.ai/api/search-image?query=Distinguished%20man%20homeowner%20at%20Wynberg%20Edwardian%20house%20with%20restored%20painted%20exterior%2C%20satisfied%20client%20portrait%2C%20heritage%20architecture&width=80&height=80&seq=wynberg2&orientation=squarish'
    },
    {
      name: 'Sarah Williams',
      property: 'Family Home',
      rating: 5,
      text: 'Professional service from start to finish. They transformed our Wynberg home beautifully and worked around our family schedule. The quality is excellent and the price was fair.',
      image: 'https://readdy.ai/api/search-image?query=Happy%20woman%20homeowner%20in%20Wynberg%20family%20home%20with%20freshly%20painted%20rooms%2C%20satisfied%20customer%20portrait%2C%20modern%20interior&width=80&height=80&seq=wynberg5&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Victorian Villa Restoration',
      description: 'Complete exterior and interior restoration with period-appropriate colors, decorative details, and modern protective coatings',
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20Victorian%20villa%20in%20Wynberg%20with%20restored%20painted%20exterior%2C%20period%20architecture%2C%20decorative%20details%2C%20professional%20restoration%20work&width=400&height=300&seq=wynberg3&orientation=landscape'
    },
    {
      title: 'Edwardian Home Renovation',
      description: 'Full painting renovation preserving original character with modern finishes, including woodwork restoration and color matching',
      image: 'https://readdy.ai/api/search-image?query=Edwardian%20home%20in%20Wynberg%20with%20renovated%20painted%20exterior%2C%20heritage%20features%2C%20professional%20painting%20finish%2C%20period%20property&width=400&height=300&seq=wynberg4&orientation=landscape'
    },
    {
      title: 'Modern Family Home',
      description: 'Contemporary interior and exterior painting with neutral palette and durable finishes for busy family lifestyle',
      image: 'https://readdy.ai/api/search-image?query=Modern%20family%20home%20in%20Wynberg%20with%20freshly%20painted%20exterior%2C%20contemporary%20style%2C%20professional%20painting%20work%2C%20residential%20property&width=400&height=300&seq=wynberg6&orientation=landscape'
    },
    {
      title: 'Heritage Property Complex',
      description: 'Multi-unit heritage property maintenance program with coordinated colors and preservation standards',
      image: 'https://readdy.ai/api/search-image?query=Heritage%20property%20complex%20in%20Wynberg%20with%20maintained%20painted%20exteriors%2C%20Victorian%20architecture%2C%20professional%20maintenance%20work&width=400&height=300&seq=wynberg7&orientation=landscape'
    }
  ];

  const paintingProcess = [
    {
      step: '1',
      title: 'Heritage Assessment',
      description: 'Thorough evaluation of your Wynberg property including architectural style, historical features, and any heritage requirements or restrictions.'
    },
    {
      step: '2',
      title: 'Custom Planning',
      description: 'Development of tailored painting solutions respecting Wynberg\'s architectural heritage while meeting modern performance standards.'
    },
    {
      step: '3',
      title: 'Careful Preparation',
      description: 'Meticulous surface preparation using appropriate methods for different materials and architectural periods, ensuring optimal results.'
    },
    {
      step: '4',
      title: 'Expert Application',
      description: 'Professional painting using period-appropriate techniques and modern materials for superior finish quality and longevity.'
    },
    {
      step: '5',
      title: 'Quality Inspection',
      description: 'Detailed inspection ensuring every aspect meets our high standards and respects the property\'s architectural character.'
    },
    {
      step: '6',
      title: 'Maintenance Guidance',
      description: 'Comprehensive care instructions and maintenance recommendations specific to your Wynberg property\'s needs and style.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Victorian%20home%20in%20Wynberg%20Cape%20Town%2C%20heritage%20architecture%2C%20painting%20equipment%2C%20scaffolding%2C%20beautiful%20period%20property%2C%20sunny%20day&width=1920&height=1080&seq=wynberghero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Wynberg
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Wynberg homes and heritage properties. Specialized Victorian home painting and restoration services for your period property with respect for architectural character and modern protection standards.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Wynberg Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Heritage Painting Excellence in Wynberg
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Wynberg is one of Cape Town's most historically significant suburbs, renowned for its beautiful Victorian and Edwardian architecture, tree-lined streets, and rich cultural heritage. This established community combines historical charm with modern convenience, attracting families and professionals who appreciate the area's unique character and excellent amenities including schools, shopping, and transport links.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                The architectural diversity of Wynberg presents unique opportunities and challenges for painting services. From grand Victorian villas with ornate details to charming Edwardian cottages and modern family homes, each property requires specialized knowledge and appropriate techniques. Many of Wynberg's older properties feature distinctive architectural elements including decorative woodwork, sash windows, and period details that demand careful attention and expertise.
              </p>
              <p className="text-lg text-gray-600">
                Our team has extensive experience working with Wynberg's diverse property types and understands the importance of preserving architectural heritage while providing modern protection and finishes. We combine traditional craftsmanship with contemporary materials and techniques to deliver results that honor the past while ensuring long-lasting performance. Whether your property is a protected heritage building or a modern home, we provide tailored solutions that respect its character and meet your specific requirements.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Wynberg%20Cape%20Town%20tree-lined%20street%20with%20Victorian%20and%20Edwardian%20homes%2C%20heritage%20architecture%2C%20established%20suburb%2C%20beautiful%20period%20properties&width=600&height=400&seq=wynberg8&orientation=landscape"
                alt="Wynberg Heritage Area"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Wynberg Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions designed specifically for Wynberg's unique architectural heritage and diverse property types.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Wynberg Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring exceptional results for your Wynberg property while respecting its architectural heritage.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us for Wynberg */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Wynberg?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized expertise in Wynberg's unique architectural heritage with proven results and satisfied customers.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-building-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Heritage Expertise</h3>
              <p className="text-gray-600">Specialized knowledge of Victorian and Edwardian architecture with experience in heritage restoration and conservation standards.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-palette-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Period Color Matching</h3>
              <p className="text-gray-600">Expert color consultation and matching services for period properties, ensuring historically appropriate and aesthetically pleasing results.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-tools-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Traditional Techniques</h3>
              <p className="text-gray-600">Skilled in traditional painting methods and modern techniques, providing the best approach for each unique Wynberg property.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                <i className="ri-shield-check-line text-black text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Quality Guarantee</h3>
              <p className="text-gray-600">Comprehensive warranties and quality assurance on all Wynberg painting projects, backed by years of experience and satisfied customers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Wynberg Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our expertise in Wynberg heritage and residential painting with recent successful projects across the area.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Wynberg Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Wynberg property owners who have experienced our professional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Heritage Challenges Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Understanding Wynberg's Heritage Painting Requirements
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Specialized Considerations We Address</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-building-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Architectural Preservation</h4>
                    <p className="text-gray-600">Careful attention to preserving original architectural details and character while providing modern protection and performance.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-palette-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Historical Color Accuracy</h4>
                    <p className="text-gray-600">Research and matching of period-appropriate colors for Victorian and Edwardian properties, respecting historical authenticity.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-tools-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Woodwork Restoration</h4>
                    <p className="text-gray-600">Specialized techniques for restoring and protecting Wynberg's characteristic wooden features including sash windows and decorative trim.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-shield-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Modern Protection Standards</h4>
                    <p className="text-gray-600">Application of contemporary protective coatings and techniques that provide superior performance while respecting heritage requirements.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Wynberg%20Victorian%20home%20architectural%20details%2C%20decorative%20woodwork%2C%20sash%20windows%2C%20period%20features%2C%20heritage%20restoration%20work&width=600&height=500&seq=wynberg9&orientation=portrait"
                alt="Wynberg Heritage Features"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your Wynberg Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's heritage painting specialists for Wynberg. Our expert team understands the unique requirements of Victorian and Edwardian properties and is ready to provide you with a comprehensive consultation and detailed quote. Experience the difference that specialized heritage painting expertise makes for your Wynberg home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Elfsight Popup */}
      <div className="elfsight-app-a8d09b4b-1f9c-47af-9c95-257e6e1aa6a7" data-elfsight-app-lazy></div>
    </div>
  );
}
